import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const stars = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);

// טאב "דירוגים ותגובות" - ניהול כל התגובות שהלקוחות כתבו על בתי עסק (רק
// מי שמימש קוד יכול לכתוב, ראו client/app/components/ReviewModal.tsx).
// admin בלבד (נאכף גם בשרת, spBOGetBusinessReviews/spBOToggleBusinessReviewActive).
// "הסתרה"/"שחזור" = active=0/1, לא מחיקה - אותה מוסכמה כמו הודעות/בתי עסק,
// כדי שיהיה תיעוד ואפשר לבטל טעות
const BusinessReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHidden, setShowHidden] = useState(true);
  const [search, setSearch] = useState('');
  const [toggleBusyId, setToggleBusyId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchBusinessReviews(true); // תמיד מביאים הכל - הסינון לפי showHidden נעשה בצד הלקוח כדי לא לטעון מחדש בכל טוגל
      if (resp?.[0]?.[0]?.code === 0) setReviews(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת התגובות');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (review) => {
    setToggleBusyId(review.id);
    setError('');
    try {
      const resp = await BOAPI.toggleBusinessReviewActive(review.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const active = !!resp[1]?.[0]?.active;
        setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, active } : r)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setToggleBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reviews.filter((r) => {
      if (!showHidden && !r.active) return false;
      if (!q) return true;
      return `${r.businessName} ${r.authorName} ${r.comment || ''}`.toLowerCase().includes(q);
    });
  }, [reviews, showHidden, search]);

  const hiddenCount = reviews.filter((r) => !r.active).length;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>דירוגים ותגובות</h2>
        <span style={s.totalBadge}>{reviews.length} תגובות{hiddenCount > 0 ? ` · ${hiddenCount} מוסתרות` : ''}</span>
      </div>
      <p style={s.hint}>
        רק לקוח שמימש קוד בבית העסק יכול לכתוב תגובה, בתוך חלון זמן קצר מהמימוש. הסתרת תגובה לא מוחקת אותה - אפשר לשחזר בכל עת.
      </p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}

      <div style={s.toolbar}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.searchInput}
          placeholder="חיפוש לפי בית עסק, כותב או תוכן..."
        />
        <label style={s.checkboxLabel}>
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          הצגת תגובות מוסתרות
        </label>
      </div>

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : filtered.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>⭐</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>לא נמצאו תגובות</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow2}>
                <th style={s.th}>בית עסק</th>
                <th style={s.th}>כותב</th>
                <th style={s.th}>שירות</th>
                <th style={s.th}>מחיר</th>
                <th style={s.th}>תגובה</th>
                <th style={s.th}>תאריך</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} style={s.row}>
                  <td style={s.td}>{r.businessName}</td>
                  <td style={s.td}>{r.authorName}</td>
                  <td style={s.td}><span style={s.starsText}>{stars(r.serviceRating)}</span></td>
                  <td style={s.td}><span style={s.starsText}>{stars(r.priceRating)}</span></td>
                  <td style={s.td}><span style={s.commentPreview}>{r.comment || '—'}</span></td>
                  <td style={s.td}>{fmtDate(r.iTime)}</td>
                  <td style={s.td}>
                    <span style={{ ...s.statusBadge, ...(r.active ? s.statusActive : s.statusInactive) }}>
                      {r.active ? 'פעיל' : 'מוסתר'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <button
                      style={r.active ? s.deactivateBtn : s.activateBtn}
                      onClick={() => toggleActive(r)}
                      disabled={toggleBusyId === r.id}
                    >
                      {toggleBusyId === r.id ? '...' : r.active ? 'הסתרה' : 'שחזור'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },

  toolbar:      { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  searchInput:  { flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, boxSizing: 'border-box', direction: 'rtl' },
  checkboxLabel:{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4f566b', cursor: 'pointer', whiteSpace: 'nowrap' },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5' },
  td:           { padding: '10px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  starsText:    { color: '#EFA518', letterSpacing: 1, fontSize: 14 },
  commentPreview: { display: 'inline-block', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive:{ background: '#fff1f1', color: '#d93838' },
  activateBtn:  { padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn:{ padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};

export default BusinessReviewsTab;
